export const SERVICE_NAV = [
  { label: "Student Counselling", anchor: "student-counselling" },
  { label: "University Selection", anchor: "university-selection" },
  { label: "Application Assistance", anchor: "application-support" },
  { label: "Scholarship Guidance", anchor: "scholarship-guidance" },
  { label: "Visa Assistance", anchor: "visa-guidance" },
  { label: "Pre-Departure Support", anchor: "pre-departure-support" },
  { label: "Post-Arrival Support", anchor: "post-arrival-support" },
];

export const WHY_SUVERA = [
  {
    icon: "UserCheck",
    title: "Personalised Guidance",
    text: "We take time to understand each student's academic background, goals and preferred destination before recommending anything.",
  },
  {
    icon: "Globe2",
    title: "Global Opportunities",
    text: "Explore universities and institutions across nine international destinations, from foundation to postgraduate level.",
  },
  {
    icon: "Route",
    title: "End-to-End Support",
    text: "From the first counselling session through applications, visa preparation and departure — one team, the whole way.",
  },
  {
    icon: "FileCheck2",
    title: "Transparent Process",
    text: "Clear information about applications, documentation, timelines and next steps at every stage of your journey.",
  },
];

export const JOURNEY_STEPS = [
  { step: "01", title: "Discover", text: "Understand your academic profile, interests and career goals." },
  { step: "02", title: "Choose Your Destination", text: "Compare countries on education, courses, costs and opportunities." },
  { step: "03", title: "Select Your Institution", text: "Identify universities and institutions that fit your goals." },
  { step: "04", "title": "Apply", text: "Prepare documents and submit complete, well-presented applications." },
  { step: "05", title: "Prepare for Visa", text: "Receive guidance on visa documentation and preparation." },
  { step: "06", title: "Begin Your Journey", text: "Get pre-departure guidance and step confidently into your new life." },
];

export const SERVICES = [
  {
    id: "education-counselling",
    icon: "MessagesSquare",
    title: "Education Counselling",
    short: "One-to-one sessions to understand your goals and map realistic study-abroad options.",
    long: "Every journey begins with a conversation. Our advisors take the time to understand your academic history, interests, budget and career ambitions before suggesting a single destination or course. The result is a plan built around you — not a template.",
    points: ["Personalised profile assessment", "Career and course direction", "Honest, unbiased advice"],
  },
  {
    id: "course-selection",
    icon: "BookOpenCheck",
    title: "Course Selection",
    short: "Identify programs that match your academic strengths and career direction.",
    long: "The right course matters as much as the right university. We help you compare curricula, specialisations, teaching styles and career outcomes so the program you choose genuinely moves you toward the future you want.",
    points: ["Curriculum and outcome comparison", "Specialisation guidance", "Career-aligned recommendations"],
  },
  {
    id: "destination-selection",
    icon: "MapPinned",
    title: "Destination Selection",
    short: "Compare countries on education quality, cost, lifestyle and opportunity.",
    long: "Each destination offers a different balance of academic strength, cost, work rights and lifestyle. We walk you through a structured comparison so your country choice is a decision made with clarity, not guesswork.",
    points: ["Structured country comparison", "Cost and lifestyle insight", "Work rights and pathways explained"],
  },
  {
    id: "university-selection",
    icon: "GraduationCap",
    title: "University Selection",
    short: "Shortlist institutions aligned with your academic and career goals.",
    long: "From thousands of institutions we build a focused shortlist matched to your grades, budget and ambitions — balancing aspiration with realism so every application counts.",
    points: ["Tailored institution shortlists", "Entry requirement matching", "Reach, match and safe balance"],
  },
  {
    id: "application-support",
    icon: "FileText",
    title: "Application Support",
    short: "Complete, well-presented applications submitted on time.",
    long: "Strong applications are complete, consistent and on time. We guide you through forms, statements, references and deadlines, reviewing everything before submission so nothing is left to chance.",
    points: ["Application form guidance", "Statement of purpose review", "Deadline management"],
  },
  {
    id: "documentation-assistance",
    icon: "FolderCheck",
    title: "Documentation Assistance",
    short: "Organise transcripts, references and supporting documents correctly.",
    long: "Missing or incorrectly formatted documents are the most common cause of delays. We help you gather, verify and organise every document to each institution's exact specification.",
    points: ["Document checklists", "Formatting and certification guidance", "Translation coordination"],
  },
  {
    id: "scholarship-guidance",
    icon: "Award",
    title: "Scholarship Guidance",
    short: "Identify scholarship opportunities you are genuinely competitive for.",
    long: "We help you find scholarships matched to your profile and present your strengths effectively — from merit awards to institutional grants — with realistic advice about your chances.",
    points: ["Opportunity identification", "Application strategy", "Essay and statement support"],
  },
  {
    id: "visa-guidance",
    icon: "Stamp",
    title: "Visa Guidance",
    short: "Step-by-step support through student visa documentation and preparation.",
    long: "Visa rules differ by destination and change often. We guide you through documentation, financial evidence and interview preparation so your application tells one clear, consistent story.",
    points: ["Document preparation", "Financial evidence guidance", "Interview preparation"],
  },
  {
    id: "pre-departure-support",
    icon: "Plane",
    title: "Pre-Departure Support",
    short: "Briefings on travel, accommodation, banking and arrival.",
    long: "Our pre-departure briefings cover everything from packing and insurance to banking, accommodation check-in and local registration — so you land prepared, not guessing.",
    points: ["Pre-departure briefings", "Accommodation guidance", "Travel and insurance checklist"],
  },
  {
    id: "post-arrival-support",
    icon: "HeartHandshake",
    title: "Post-Arrival Support",
    short: "Continued guidance as you settle into your new destination.",
    long: "Our support does not end at the airport. We stay available as you settle in — helping with the practical questions that arise in your first weeks abroad.",
    points: ["Settling-in guidance", "Local registration support", "Ongoing advisor access"],
  },
];

export const HOME_SERVICES = [
  { icon: "Compass", title: "Career & Course Counselling", text: "Understand course options and career pathways with expert guidance." },
  { icon: "GraduationCap", title: "University Selection", text: "Identify institutions aligned with your academic and career goals." },
  { icon: "FileText", title: "Application Support", text: "Guidance through every application requirement and deadline." },
  { icon: "FolderCheck", title: "Documentation Assistance", text: "Organise academic and application documentation correctly." },
  { icon: "Award", title: "Scholarship Guidance", text: "Identify scholarship opportunities relevant to your profile." },
  { icon: "Stamp", title: "Visa Guidance", text: "Support throughout the student visa preparation process." },
  { icon: "Plane", title: "Pre-Departure Support", text: "Prepare for travel, arrival and the transition ahead." },
  { icon: "HeartHandshake", title: "Post-Arrival Guidance", text: "Settle into your destination with continued support." },
];

export const PATHWAY_STEPS = [
  { level: "Stage 01", title: "School / O-Level", text: "Your starting point — secondary education results open the door to pathway programs.", color: "#E63946" },
  { level: "Stage 02", title: "Foundation", text: "Build academic and English foundations for university entry.", color: "#E9C46A" },
  { level: "Stage 03", title: "Diploma", text: "Gain subject knowledge and a recognised qualification.", color: "#2A9D8F" },
  { level: "Stage 04", title: "Higher Diploma", text: "Advance toward degree-level study, often with second-year entry.", color: "#4EA8DE" },
  { level: "Stage 05", title: "Bachelor's Degree", text: "Progress into your degree at a partner university.", color: "#1B2F5B" },
  { level: "Stage 06", title: "Career", text: "Graduate ready for the global job market.", color: "#E63946" },
];

export const FAQS = [
  {
    q: "How do I choose the right country?",
    a: "The right country depends on your field of study, budget, career goals and lifestyle preferences. We compare destinations with you across education quality, costs, work rights and post-study opportunities so the decision is based on evidence, not trends.",
  },
  {
    q: "How do I choose a university?",
    a: "We shortlist institutions based on your academic profile, preferred course, budget and goals — balancing aspirational choices with realistic ones so every application has genuine potential.",
  },
  {
    q: "Can Suvera Academy help with applications?",
    a: "Yes. We guide you through application forms, statements of purpose, references and deadlines, and review every document before submission.",
  },
  {
    q: "Do you provide scholarship guidance?",
    a: "Yes. We identify scholarships matched to your academic profile and help you prepare competitive applications, with honest advice about eligibility and chances.",
  },
  {
    q: "Do you help with visa preparation?",
    a: "Yes. We guide you through visa documentation, financial evidence and interview preparation specific to your destination country's requirements.",
  },
  {
    q: "What documents are required?",
    a: "Typically: academic transcripts and certificates, English proficiency scores, a statement of purpose, letters of recommendation, a valid passport and financial documentation. Exact requirements vary by institution — we provide a tailored checklist.",
  },
  {
    q: "Can I study abroad after completing a diploma?",
    a: "Yes. Many universities offer advanced standing or credit transfer for diploma holders, allowing entry into the second year of a related degree. Pathway options depend on your qualification and destination.",
  },
  {
    q: "Do you provide pre-departure guidance?",
    a: "Yes. Our pre-departure briefings cover travel, accommodation, insurance, banking, packing and what to expect on arrival in your destination.",
  },
  {
    q: "Can I book a counselling session?",
    a: "Absolutely. Use the Book Free Consultation button anywhere on this site, or contact us directly, and an education advisor will arrange a session at a time that suits you.",
  },
  {
    q: "Which countries do you support?",
    a: "We currently support study destinations across the USA, UK, Canada, Australia, New Zealand, Germany, Singapore, Malaysia and Mauritius.",
  },
];

export const VALUES = [
  { icon: "ShieldCheck", title: "Integrity", text: "Honest advice, always — even when it is not what you expected to hear." },
  { icon: "Heart", title: "Student First", text: "Every recommendation starts with the student's goals, not our convenience." },
  { icon: "Eye", title: "Transparency", text: "Clear information about processes, timelines and realistic outcomes." },
  { icon: "Sparkles", title: "Excellence", text: "High standards in every application, document and interaction." },
  { icon: "Globe2", title: "Global Perspective", text: "Deep knowledge of international education systems and cultures." },
  { icon: "UserCheck", title: "Personalised Guidance", text: "No templates — advice shaped around each individual student." },
];

export const TESTIMONIALS = [
  {
    name: "Aarav Menon",
    program: "MSc Data Science",
    destination: "United Kingdom",
    quote: "Suvera Academy made my UK application feel effortless — from shortlisting universities to the visa interview, every step was clearly explained and honestly guided.",
  },
  {
    name: "Nisha Fernando",
    program: "Diploma in Business",
    destination: "Singapore",
    quote: "After my O-Levels I thought studying abroad was out of reach. The team matched me with a pathway programme that fit my results perfectly — I'm now studying in Singapore.",
  },
  {
    name: "Daniel D'Souza",
    program: "BBA",
    destination: "Mauritius",
    quote: "Their scholarship guidance genuinely reduced our costs. Transparent, patient, and always available whenever we had questions.",
  },
];

export const INTAKES = ["January 2027", "May 2027", "September 2026", "September 2027", "Not sure yet"];

export const STUDY_LEVELS = ["Foundation", "Diploma", "Undergraduate", "Postgraduate", "Doctorate", "Not sure yet"];

export const EDUCATION_LEVELS = [
  "O-Level / Secondary School",
  "A-Level / High School",
  "Foundation",
  "Diploma",
  "Higher Diploma",
  "Bachelor's Degree",
  "Master's Degree",
  "Other",
];
